import { ERROR_MESSAGES } from 'constants/errorMessages'

import { Rule } from 'antd/lib/form'
import moment from 'moment'

const required: Rule = {
  validator: (_, value) => {
    if (value === undefined || value === null)
      return Promise.reject(new Error(ERROR_MESSAGES.REQUIRED_FIELD))

    const trimValue = `${value}`.trim()
    return trimValue ? Promise.resolve() : Promise.reject(new Error(ERROR_MESSAGES.REQUIRED_FIELD))
  },
}

const requiredWithMsg: (msg: string) => Rule = (msg) => ({
  validator: (_, value) => {
    if (value === undefined || value === null) return Promise.reject(new Error(msg))

    const trimValue = `${value}`.trim()
    return trimValue ? Promise.resolve() : Promise.reject(new Error(msg))
  },
})

const mobilePhone: Rule = {
  required: true,
  pattern: /^[0][3,5,7,8,9]\d{8}$/,
  message: ERROR_MESSAGES.INVALID_PHONE,
}

const mobilePhoneIncome: Rule = {
  required: true,
  pattern: /^[0]\d{9,19}$/,
  message: ERROR_MESSAGES.INVALID_PHONE_INCOME,
}
const mobilePhoneIncomeOptional: Rule = {
  required: false,
  pattern: /^[0]\d{9,19}$/,
  message: ERROR_MESSAGES.INVALID_PHONE_INCOME,
}

const idNo: Rule[] = [
  { required: true, pattern: /^\d{0,12}$/, message: ERROR_MESSAGES.INVALID_ID_NO },
]

const id_cccd_tcc: Rule = {
  pattern: /^\d{12}$/,
  message: 'Chỉ được nhập số và phải đúng 12 ký tự',
}

const id_cmnd: Rule = {
  pattern: /^(\d{9}|\d{12})$/,
  message: 'Chỉ được nhập số và phải đúng 9 hoặc 12 ký tự',
}

const id_other: Rule = {
  required: true,
  pattern: /^[a-zA-Z0-9-]+$/,
  message: 'Chỉ được nhập chữ không dấu, số và dấu gạch ngang',
}

// Custom ruleIdentification function
export const ruleIdentification = (
  typeIdentificationFieldName: string,
  getFieldValue: (field: string) => any
): Rule => ({
  validator: (_, value) => {
    // Xử lý required trước
    if (!value) {
      return Promise.reject(new Error(ERROR_MESSAGES.REQUIRED_FIELD))
    }

    const typeIdentification = getFieldValue(typeIdentificationFieldName) // Lấy giá trị của field

    // Xử lý required trước
    if (!typeIdentification) {
      return Promise.reject(new Error('Vui lòng chọn loại giấy tờ tùy thân trước'))
    }

    // Xác định rule dựa trên giá trị của typeIdentification
    let ruleToApply: Rule
    if (typeIdentification === 'CC' || typeIdentification === 'IDC') {
      ruleToApply = id_cccd_tcc
    } else if (typeIdentification === 'CMND') {
      ruleToApply = id_cmnd
    } else {
      ruleToApply = id_other
    }

    // Kiểm tra pattern đã được định nghĩa trong rule tương ứng
    if (ruleToApply.pattern && !ruleToApply.pattern.test(value)) {
      return Promise.reject(new Error(ruleToApply.message as any))
    }

    return Promise.resolve()
  },
})

// Custom rule for required if one has value - old_id_no and old_id_type
export const requiredIfOneHasValue = (
  fieldName: string,
  getFieldValue: (field: string) => any
): Rule => ({
  validator: (_, value) => {
    const other = getFieldValue(fieldName)

    if (!value && other) {
      return Promise.reject(
        'Loại giấy tờ tùy thân cũ và số CMND/CCCD cũ phải được cung cấp cùng nhau'
      )
    }
    return Promise.resolve()
  },
})

const oldIdNo = (idNoName: string): Rule[] => [
  {
    required: false,
    pattern: /^\d{0,12}$/,
    message: ERROR_MESSAGES.INVALID_OLD_ID_NO,
  },
]

const greaterThan0: Rule = {
  required: false,
  pattern: /^[1-9][0-9]*$/,
  message: ERROR_MESSAGES.GREATER_THAN_0,
}

const pastDay = (message?: string, equal?: boolean): Rule => ({
  validator(_, value) {
    const today = moment(new Date()).startOf('day').toDate().getTime()
    const pickedDate = moment(value).startOf('day').toDate().getTime()

    if (equal && pickedDate <= today) {
      return Promise.resolve()
    }

    if (!equal && pickedDate < today) {
      return Promise.resolve()
    }
    return Promise.reject(new Error(message || 'Vui lòng nhập nhỏ hơn ngày hiện tại'))
  },
})

const futureDay = (message?: string, equal?: boolean): Rule => ({
  validator(_, value) {
    const today = moment(new Date()).startOf('day').toDate().getTime()
    const pickedDate = moment(value).startOf('day').toDate().getTime()

    if (equal && pickedDate >= today) {
      return Promise.resolve()
    }

    if (!equal && pickedDate > today) {
      return Promise.resolve()
    }
    return Promise.reject(new Error(message || 'Vui lòng nhập nhỏ hơn ngày hiện tại'))
  },
})

const greaterDateThanField =
  (fieldName: string, message?: string): Rule =>
  ({ getFieldValue }) => ({
    validator(_, value) {
      const fieldValue = getFieldValue(fieldName) as string
      if (!fieldValue) {
        return Promise.resolve()
      }
      const fieldValueDate = moment(fieldValue).startOf('day').toDate().getTime()
      const pickedDate = moment(value).startOf('day').toDate().getTime()
      if (pickedDate >= fieldValueDate) {
        // eslint-disable-next-line no-template-curly-in-string
        return Promise.reject(new Error(message || ' Vui lòng nhập nhỏ hơn ${label}'))
      }

      return Promise.resolve()
    },
  })
const minValue =
  (minValue: number, equal?: boolean, message?: string): Rule =>
  ({ getFieldValue }) => ({
    validator(_, value) {
      if (Number.isNaN(value)) {
        return Promise.reject(new Error('Vui lòng nhập đúng định dạng'))
      }

      if (equal && value < minValue) {
        // eslint-disable-next-line no-template-curly-in-string
        return Promise.reject(new Error(message || ' Vui lòng nhập nhỏ hơn hoặc bằng ${label}'))
      }

      if (!equal && value <= minValue) {
        // eslint-disable-next-line no-template-curly-in-string
        return Promise.reject(new Error(message || ' Vui lòng nhập nhỏ hơn ${label}'))
      }

      return Promise.resolve()
    },
  })
const maxValue =
  (maxValue: number, equal?: boolean, message?: string): Rule =>
  ({ getFieldValue }) => ({
    validator(_, value) {
      if (Number.isNaN(value)) {
        return Promise.reject(new Error('Vui lòng nhập đúng định dạng'))
      }

      if (equal && value > maxValue) {
        // eslint-disable-next-line no-template-curly-in-string
        return Promise.reject(new Error(message || ' Vui lòng nhập lớn hơn hoặc bằng ${label}'))
      }

      if (!equal && value >= maxValue) {
        // eslint-disable-next-line no-template-curly-in-string
        return Promise.reject(new Error(message || ' Vui lòng nhập lớn hơn ${label}'))
      }

      return Promise.resolve()
    },
  })

const commonValidations = {
  required,
  mobilePhone,
  mobilePhoneIncome,
  mobilePhoneIncomeOptional,
  idNo,
  oldIdNo,
  greaterThan0,
  pastDay,
  greaterDateThanField,
  futureDay,
  minValue,
  maxValue,
  requiredWithMsg,
}

export { commonValidations }
